/**
 * This slice is dedicated to Authentication API data
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import config from '../../../config';
import LibraryConfig from '../../../configLib';
import { CallStatus, RecoverPhaseEnum } from '../../enums';
import { IGNORE_GATEWAY_CONNECTION } from '../../libSettings';
import {
  ExtraUserDetails,
  RecoveryDataPayload,
  SignupTokenData,
} from '../../models/auth/types';
import {
  ENROLL_REQUEST_STATE,
  EnrollAuthDataState,
  IndividualExtendedInfoData,
} from '../../models/enroll';
import {
  ENROLL_AUTH_REQUEST_TYPE,
  EnrollAuthRequestBody,
  REQUEST_CALL_NAME_CONFIRM_EMAIL,
  REQUEST_CALL_NAME_REDEEM_SIGNUP_TOKEN,
} from '../../models/enroll-requests';
import { NullableNumber, NullableString, WebAuthUserData } from '../../util/types';
import {
  applicationStartup,
  enrollAuthCompleted,
  ErrorPayloadFull,
  gatewayConnected,
  gatewayDisconnected,
  individualExtendedInfoFailed,
  individualExtendedInfoSuccess,
  login,
  loginFailed,
  logout,
} from '../common-actions';
import { parseOnboardingErrorCode } from '../crm/helpers';
import { processErrorInReducer } from '../helpers';
import {
  getEnrollRecoveryPhase,
  getRecoverPhase,
  hasAnsweredLastQuestionnaire,
  isUserVerifiedStatus,
} from '../selectors';
import { LoggerService } from '../../services/logger/Logger.service';

export interface AuthState {
  status: 'idle' | 'refreshing' | 'connecting' | 'ready' | 'cancelled' | 'redirectsignup';
  callBackPending: boolean;
  skipRefresh: boolean;
  isChangeUserPassword: boolean;
  enroll: EnrollAuthDataState;
  enrollUserId: NullableNumber;
  enrollPending: boolean;
  enrollRecoveryChecked: boolean;
  recoveryPhase?: RecoverPhaseEnum | null;
  recoveryStep?: NullableNumber;
  recoverySubStep?: NullableNumber;
  error: string | null;
  errorData: any;
  userData?: WebAuthUserData | null;
  isPasscodeActive: boolean;
  individualAvatar: string;
  isForgetPasscode: boolean;
  passcode: NullableString;
  isBiometricsActive: boolean;
  isNeedPasscodeLogin: boolean;
  createIndividualAttempts: number;
  isAppropriatenessPopupConfirmed: boolean;
  PWPSignupURL: NullableString;
  isFetchingPWPSignupURL: boolean;
  isForceRedirectToEnroll: boolean;
  enrollPortalStatus: Record<string, number>;
  cookieSessionValid: boolean | null;
  bffClaims: Array<{ type: string; value: any }> | null;
}

export const INITIAL_STATE: AuthState = {
  status: 'idle',
  callBackPending: false,
  skipRefresh: false,
  isChangeUserPassword: false,
  enroll: {
    status: ENROLL_REQUEST_STATE.INITIAL,
    confirmedSteps: {},
  } as EnrollAuthDataState,
  enrollUserId: null,
  enrollPending: false,
  enrollRecoveryChecked: false,
  error: null,
  errorData: null,
  userData: null,
  isPasscodeActive: false,
  individualAvatar: '',
  isForgetPasscode: false,
  passcode: null,
  isBiometricsActive: false,
  isNeedPasscodeLogin: true,
  createIndividualAttempts: 0,
  isAppropriatenessPopupConfirmed: false,
  PWPSignupURL: null,
  isFetchingPWPSignupURL: false,
  isForceRedirectToEnroll: false,
  enrollPortalStatus: {
    status: CallStatus.INITIAL,
  },
  cookieSessionValid: null,
  bffClaims: null,
};

const logger = LoggerService.getLogger();

// TODO: The web part needs to call the same method for expired link check as well.
const isLinkExpired = (state: EnrollAuthDataState): boolean => {
  const { requestCallName } = state;

  return (
    state.status === ENROLL_REQUEST_STATE.ERROR
    && parseOnboardingErrorCode(state.statusCode!) === ENROLL_REQUEST_STATE.REQUEST_ERROR
    && [ REQUEST_CALL_NAME_CONFIRM_EMAIL, REQUEST_CALL_NAME_REDEEM_SIGNUP_TOKEN ].includes(requestCallName!));
};

const authSlice = createSlice({
  name: 'auth',
  initialState: INITIAL_STATE,
  reducers: {
    loginSucceeded(state, action) {
      const {
        userData,
      } = action.payload;

      if (state.isPasscodeActive && state.isForgetPasscode) {
        state.isForgetPasscode = false;
      }

      state.userData = userData;
      state.status = 'ready';
      if (state.enrollPending) {
        state.enroll.status = ENROLL_REQUEST_STATE.READY;
      }
    },
    setRecoveryPhase(state, action: PayloadAction<RecoverPhaseEnum>) {
      state.recoveryPhase = action.payload;
    },
    setRecoveryData(state, action: PayloadAction<RecoveryDataPayload>) {
      const {
        callType,
        // extended info
        extendedInfo,
        // email-sent
        tokenToConfirm,
        emailToConfirm,
        firstName,
        lastName,
        country,
        financialQuestionnaireSections,
      } = action.payload || {};

      if (callType === 'recovered') {
        state.enrollRecoveryChecked = true;
        return;
      }

      const isAppropriatenessPopupConfirmed = state.isAppropriatenessPopupConfirmed;

      const recoverPhase = getRecoverPhase(
        extendedInfo!,
        financialQuestionnaireSections,
        !!isAppropriatenessPopupConfirmed,
      );

      switch (callType) {
        case 'email-sent':
          state.enroll.emailToConfirm = emailToConfirm!;
          state.enroll.tokenToConfirm = tokenToConfirm!;
          state.enroll.firstName = firstName;
          state.enroll.lastName = lastName;
          state.enroll.country = country;
          state.recoveryPhase = recoverPhase;
          break;

        case 'extended-info': {
          if (recoverPhase === RecoverPhaseEnum.Enroll) {
            const { phase, step } = getEnrollRecoveryPhase(extendedInfo!);
            state.recoveryStep = phase;
            state.recoverySubStep = step;
          }
          state.recoveryPhase = recoverPhase;
          break;
        }

        default:
          break;
      }
    },
    getUserPermissions(state, action) { },
    getUserPermissionsSucceeded(state, action) { },
    getUserPermissionsFailed(state, action) { },
    getPermissions(state, action) { },
    getPermissionsSucceeded(state, action) { },
    getPermissionsFailed(state, action) { },
    enrollAuth(state, action) {
      const { requestType } = action.payload as EnrollAuthRequestBody;
      state.enroll.lastType = requestType;

      switch (requestType) {
        // TODO: Remove this enum value and related code with ART-1023
        case ENROLL_AUTH_REQUEST_TYPE.RECOVER: {
          state.enrollPending = true;
          state.enroll.status = ENROLL_REQUEST_STATE.READY;
          return;
        }

        case ENROLL_AUTH_REQUEST_TYPE.REGISTER:
        case ENROLL_AUTH_REQUEST_TYPE.RESEND_EMAIL: {
          const {
            first_name,
            last_name,
            country_of_residence,
          } = (action.payload?.extraDetails || {}) as ExtraUserDetails;
          if (requestType === ENROLL_AUTH_REQUEST_TYPE.REGISTER && first_name && last_name && country_of_residence) {
            state.enroll.firstName = first_name;
            state.enroll.lastName = last_name;
            state.enroll.country = country_of_residence;
          }
          state.enrollRecoveryChecked = true;
          state.enroll.email = action.payload.email;
          if (country_of_residence) {
            state.enroll.tokenToConfirm = undefined;
            state.enroll.emailToConfirm = undefined;
            state.enroll.confirmedSteps = {};
          }
          break;
        }

        case ENROLL_AUTH_REQUEST_TYPE.CONFIRM_EMAIL: {
          const { firstName, lastName, country } = action.payload;
          state.enrollPending = true;
          state.enroll.confirmedSteps.email = false;
          if (firstName) { // fixes ART-1314 - the data is deleted if not present in payload
            state.enroll.firstName = firstName;
            state.enroll.lastName = lastName;
            state.enroll.country = country;
          }
          break;
        }
        case ENROLL_AUTH_REQUEST_TYPE.REDEEM_SIGNUP_TOKEN: {
          const {
            username, firstName, lastName, country,
          } = action.payload;
          state.isForceRedirectToEnroll = true;
          state.enrollPending = true;
          state.enroll.email = username;
          state.enroll.confirmedSteps.email = false;
          if (firstName) {
            state.enroll.firstName = firstName;
            state.enroll.lastName = lastName;
            state.enroll.country = country;
          }
          break;
        }
        case ENROLL_AUTH_REQUEST_TYPE.SEND_PHONE_CODE: {
          const { phoneCode, phoneNumber, userId } = action.payload;
          if (userId) {
            state.enroll.userId = userId;
            state.enrollUserId = userId;
          }
          state.enroll.phoneCode = phoneCode;
          state.enroll.phoneNumber = phoneNumber;
          break;
        }

        default:
          break;
      }
      state.enroll.status = ENROLL_REQUEST_STATE.PENDING;
    },
    enrollAuthFailed(state, action) {
      state.enroll.statusCode = action.payload.status;
      state.enroll.status = ENROLL_REQUEST_STATE.ERROR;
      state.enroll.requestCallName = action.payload.callName;
      state.enroll.isAuthLinkExpired = isLinkExpired(state.enroll);
      logger.error(action.payload.errorStr);
    },
    clearEnrollStatus(state) {
      state.enroll.statusCode = undefined;
      state.enroll.confirmedSteps.didSendPhone = undefined;
      state.enroll.status = ENROLL_REQUEST_STATE.READY;
    },
    resetPasscode(state) {
      state.passcode = null;
    },
    setIsPasscodeActive(state, action) {
      state.isPasscodeActive = action.payload;
    },
    setIndividualAvatar(state, action) {
      state.individualAvatar = action.payload;
    },
    setIsForgetPasscode(state, action) {
      state.isForgetPasscode = action.payload;
    },
    setPasscode(state, action) {
      state.passcode = action.payload;
    },
    setIsBiometricsActive(state, action) {
      state.isBiometricsActive = action.payload;
    },
    setIsNeedPasscodeLogin(state, action) {
      state.isNeedPasscodeLogin = action.payload;
    },
    clearSignupToken(state, action: PayloadAction) {
      state.isFetchingPWPSignupURL = false;
      state.PWPSignupURL = null;
    },
    getSignupToken(state, action: PayloadAction) {
      state.isFetchingPWPSignupURL = true;
    },
    getSignupTokenFailed(state, action: PayloadAction<SignupTokenData>) {
      state.isFetchingPWPSignupURL = false;
      state.PWPSignupURL = null;
    },
    getSignupTokenSuccess(state, action: PayloadAction<SignupTokenData>) {
      const { payload } = action;
      state.isFetchingPWPSignupURL = false;
      state.PWPSignupURL = `${config.pwp.url}authentication/signup-token?email=${encodeURIComponent(payload.email)}&token=${encodeURIComponent(payload.token)}`;
    },
    sendResetPassword(state, action) {},
    setIsChangeUserPassword(state, action) {
      state.isChangeUserPassword = action.payload;
    },
    sendNonAppropriatenessEmail(state: AuthState) {
      state.enroll.status = ENROLL_REQUEST_STATE.INITIAL;
    },
    sendNonAppropriatenessEmailSuccess(state: AuthState) {
      state.enroll.status = ENROLL_REQUEST_STATE.READY;
    },
    sendNonAppropriatenessEmailFail(state: AuthState, action: PayloadAction<ErrorPayloadFull>) {
      state.enroll.status = ENROLL_REQUEST_STATE.ERROR;
      state.enroll.statusCode = action.payload.status;
    },
    confirmAppropriatenessPopup(state: AuthState) {
      state.isAppropriatenessPopupConfirmed = true;
    },
    setCookieSessionValid(state: AuthState, action: PayloadAction<boolean>) {
      state.cookieSessionValid = action.payload;
    },
    setBffClaims(state: AuthState, action: PayloadAction<Array<{ type: string; value: any }>>) {
      state.bffClaims = action.payload;
    },
  },
  extraReducers: {
    [applicationStartup.type]: (state, action) => {
      state.status = 'idle';
      state.enroll.status = ENROLL_REQUEST_STATE.INITIAL; // needed to reset enroll status and use persisted enroll data
      state.enroll.emailToConfirm = undefined;
      state.enroll.tokenToConfirm = undefined;
      state.recoveryPhase = null;
      state.recoveryStep = null;
      state.recoverySubStep = null;
      if (action.payload) state.enrollPending = false;
    },
    [login.type]: (state, action) => {
      if (LibraryConfig.isWeb) {
        switch (action.payload) {
          case 'login':
            state.callBackPending = true;
            break;

          case 'callback':
            state.callBackPending = false;
            break;

          default:
            console.warn(`[auth/index] login - unknown case '${action.payload}'`);
            break;
        }
      }

      if (action.payload?.isEnroll) {
        state.enrollPending = true;
        state.enroll.status = ENROLL_REQUEST_STATE.PENDING;
      } else {
        state.enrollPending = false;
      }
      state.status = 'connecting';
    },
    [loginFailed.type]: (state, action) => {
      processErrorInReducer(state, action);
      state.status = 'idle';
      if (state.enrollPending) {
        state.enroll.status = ENROLL_REQUEST_STATE.ERROR;
      }
      const errorMessage = action.payload?.errorStr || action.payload?.error || 'n/a';
      const errorData = action.payload?.errorData;
      const CANCELLED_ERROR_MSG_ANDROID = 'error: user cancelled flow';
      const CANCELLED_ERROR_MSG_IOS = 'org.openid.appauth.general error -3';
      const SIGNUP_ERROR_MSG_IOS = 'error: state mismatch, expecting';
      const SIGNUP_ERROR_MSG_ANDROID = 'error: data intent is null';

      if (errorData) {
        const errorStr = errorData.toString().toLowerCase();
        if (errorStr.indexOf(CANCELLED_ERROR_MSG_IOS) >= 0 || errorStr.indexOf(CANCELLED_ERROR_MSG_ANDROID) >= 0) {
          state.status = 'cancelled';
        } else if (errorStr.indexOf(SIGNUP_ERROR_MSG_IOS) >= 0 || errorStr.indexOf(SIGNUP_ERROR_MSG_ANDROID) >= 0) {
          state.status = 'redirectsignup';
        }
      }

      logger.error(errorMessage);
    },
    [individualExtendedInfoSuccess.type]: (state, action) => {
      state.enroll.userId = (action.payload as IndividualExtendedInfoData).user_correlation_id;
      if (action.payload?.phone_number_confirmed && action.payload?.profile_name) {
        state.isForceRedirectToEnroll = false;
      }
      if (isUserVerifiedStatus(action.payload)) {
        state.enrollPending = false;
      }
      if (state.isAppropriatenessPopupConfirmed && !hasAnsweredLastQuestionnaire(action.payload)) {
        state.isAppropriatenessPopupConfirmed = false;
      }
    },
    [gatewayConnected.type]: state => {
      state.status = 'ready';
    },
    [gatewayDisconnected.type]: state => {
      if (!IGNORE_GATEWAY_CONNECTION) state.status = 'idle';
    },
    [logout.type]: (state, action) => ({
      ...INITIAL_STATE,
      enrollUserId: action.payload?.preserveEnrollUserId
        ? (state.enrollUserId ?? state.enroll.userId ?? null)
        : null,
    }),
    [enrollAuthCompleted.type]: (state, action) => {
      const { callName } = action.payload;
      const requestType = ENROLL_AUTH_REQUEST_TYPE[callName.split('/')[1]] as any;

      switch (requestType) {
        case ENROLL_AUTH_REQUEST_TYPE.REGISTER:
          state.enroll.userId = action.payload.id;
          state.enrollUserId = action.payload.id;
          break;

        case ENROLL_AUTH_REQUEST_TYPE.CONFIRM_EMAIL:
        case ENROLL_AUTH_REQUEST_TYPE.REDEEM_SIGNUP_TOKEN:
          state.enroll.confirmedSteps.email = true;
          break;

        case ENROLL_AUTH_REQUEST_TYPE.VERIFY_PHONE_CODE:
          state.enroll.confirmedSteps.phone = true;
          break;

        case ENROLL_AUTH_REQUEST_TYPE.SEND_PHONE_CODE:
          state.enroll.confirmedSteps.didSendPhone = true;
          break;

        case ENROLL_AUTH_REQUEST_TYPE.SET_CLIENTS:
          state.enroll.clients = action.payload;
          break;

        default:
          break;
      }

      state.enroll.status = ENROLL_REQUEST_STATE.READY;
    },
    [individualExtendedInfoFailed.type]: (state, action) => {
      state.createIndividualAttempts++;
    },
  },
});

export const {
  loginSucceeded,
  // enroll
  setRecoveryData,
  enrollAuth,
  enrollAuthFailed,
  clearEnrollStatus,
  resetPasscode,
  setIsPasscodeActive,
  setIndividualAvatar,
  setIsForgetPasscode,
  setPasscode,
  setIsBiometricsActive,
  setIsNeedPasscodeLogin,
  clearSignupToken,
  getSignupToken,
  getSignupTokenSuccess,
  getSignupTokenFailed,
  sendResetPassword,
  setIsChangeUserPassword,
  setRecoveryPhase,
  sendNonAppropriatenessEmail,
  sendNonAppropriatenessEmailSuccess,
  sendNonAppropriatenessEmailFail,
  confirmAppropriatenessPopup,
  setCookieSessionValid,
  setBffClaims,
} = authSlice.actions;

export default authSlice.reducer;
